/**
 * The resources overlay: a continuous mineral heatmap painted from the exact
 * noise fields the generator gated mines and quarries on
 * (`@parcelbound/domain/resource-fields`), so what the player scouts is what
 * the server will accept.
 *
 * One camera-following plane carries a 256² canvas texture. Each texel
 * samples both fields at its own lon/lat: rich ore paints orange, rich stone
 * paints concrete-blue, and everything else gets a faint grey wash that
 * desaturates the world underneath — the reference's "survey map" look.
 * Bilinear filtering supplies the soft blur for free.
 */

import {
  CanvasTexture,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
} from "three";
import {
  RESOURCE_FIELDS,
  sampleResourceField,
} from "@parcelbound/domain/resource-fields";
import type { Projection } from "./geometry";
import * as P from "./palette";

const TEX_SIZE = 256;
/** World-metre span of the plane, derived from camera distance. */
const MIN_SPAN = 1_200;
const MAX_SPAN = 60_000;
/** Deposits fade in over this range of field strength. */
const FAINT = 0.35;
const RICH = 0.85;
/** Ground shows through the grey wash between deposits. */
const WASH_ALPHA = 0.38;
const REPAINT_MIN_MS = 200;

function fieldColor(rgb: number): [number, number, number] {
  return [(rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff];
}

const ORE_RGB = fieldColor(P.ORE_HIGH);
const STONE_RGB = fieldColor(P.CONCRETE);
const WASH_RGB = fieldColor(0xdfe3da);

export class ResourceFieldLayer {
  readonly mesh: Mesh;
  private readonly canvas: HTMLCanvasElement;
  private readonly texture: CanvasTexture;
  private paintedKey = "";
  private lastPaintAt = 0;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = TEX_SIZE;
    this.canvas.height = TEX_SIZE;
    this.texture = new CanvasTexture(this.canvas);
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    const material = new MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new Mesh(new PlaneGeometry(1, 1), material);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.renderOrder = 4;
    this.mesh.visible = false;
  }

  /**
   * Follow the camera and repaint when the view moved to a new snap cell or
   * zoom bucket. `y` differs between city view (just above the accents) and
   * map view (above the political layers).
   */
  update(proj: Projection, targetX: number, targetZ: number, dist: number, y: number): void {
    const raw = Math.min(MAX_SPAN, Math.max(MIN_SPAN, dist * 4));
    // Zoom buckets keep the texel density steady without repainting per frame.
    const span = MIN_SPAN * 1.4 ** Math.round(Math.log(raw / MIN_SPAN) / Math.log(1.4));
    const snap = span / 8;
    const cx = Math.round(targetX / snap) * snap;
    const cz = Math.round(targetZ / snap) * snap;

    this.mesh.position.set(cx, y, cz);
    this.mesh.scale.set(span, span, 1);

    const key = `${span.toFixed(0)}:${cx.toFixed(0)}:${cz.toFixed(0)}`;
    const now = performance.now();
    if (key === this.paintedKey || now - this.lastPaintAt < REPAINT_MIN_MS) return;
    this.paintedKey = key;
    this.lastPaintAt = now;
    this.paint(proj, cx, cz, span);
  }

  private paint(proj: Projection, cx: number, cz: number, span: number): void {
    const ctx2d = this.canvas.getContext("2d")!;
    const image = ctx2d.createImageData(TEX_SIZE, TEX_SIZE);
    const data = image.data;
    const ore = RESOURCE_FIELDS[0]!;
    const stone = RESOURCE_FIELDS[1]!;
    const step = span / TEX_SIZE;
    const x0 = cx - span / 2;
    const z0 = cz - span / 2;

    let i = 0;
    for (let py = 0; py < TEX_SIZE; py++) {
      // Plane +v runs toward -z after the -π/2 tilt, so row 0 is far north.
      const wz = z0 + (py + 0.5) * step;
      for (let px = 0; px < TEX_SIZE; px++) {
        const wx = x0 + (px + 0.5) * step;
        const [lon, lat] = proj.toLngLat(wx, wz);
        const vOre = sampleResourceField(ore, lon, lat);
        const vStone = sampleResourceField(stone, lon, lat);

        const tOre = smooth((vOre - FAINT) / (RICH - FAINT));
        const tStone = smooth((vStone - FAINT) / (RICH - FAINT));
        let rgb: [number, number, number];
        let alpha: number;
        if (tOre <= 0 && tStone <= 0) {
          rgb = WASH_RGB;
          alpha = WASH_ALPHA;
        } else if (tOre >= tStone) {
          rgb = ORE_RGB;
          alpha = WASH_ALPHA + (0.92 - WASH_ALPHA) * tOre;
        } else {
          rgb = STONE_RGB;
          alpha = WASH_ALPHA + (0.92 - WASH_ALPHA) * tStone;
        }
        data[i++] = rgb[0];
        data[i++] = rgb[1];
        data[i++] = rgb[2];
        data[i++] = Math.round(alpha * 255);
      }
    }
    ctx2d.putImageData(image, 0, 0);
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshBasicMaterial).dispose();
    this.texture.dispose();
  }
}

function smooth(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}
